import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  RefreshCw, 
  Home,
  Bug,
  CheckCircle
} from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  copied: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
      showDetails: false,
      copied: false
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });

    // Report to Sentry with fatal severity
    Sentry.withScope((scope) => {
      scope.setTag('error_boundary', true);
      scope.setExtra('componentStack', errorInfo.componentStack);
      scope.setLevel('fatal');
      Sentry.captureException(error);
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  copyErrorToClipboard = () => {
    const { error, errorInfo } = this.state;
    const errorText = `
Error: ${error?.message || 'Unknown error'}
Stack: ${error?.stack || 'No stack trace available'}
Component Stack: ${errorInfo?.componentStack || 'No component stack available'}
Time: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy error:', err);
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails, copied } = this.state;

      // Custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-4xl w-full">
            {/* Main Error Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-white">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-white/90 mt-1">
                      We encountered an unexpected error
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Error Message */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Bug className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-red-900">
                        {/* Raw error messages are dev-only — they can leak internals (VAPT May 2026, finding #18) */}
                        {import.meta.env.DEV
                          ? (error?.message || 'An unexpected error occurred')
                          : 'An unexpected error occurred'}
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        This error has been logged and our team will investigate it.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    onClick={this.handleReset}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </button>
                  <button
                    onClick={this.handleReload}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Home className="w-4 h-4" />
                    Go to Home
                  </button>
                </div>

                {/* Expandable Error Details — dev-only: stack traces and component
                    stacks must never render in production (VAPT May 2026, finding #18).
                    Production errors remain fully visible in Sentry. */}
                {import.meta.env.DEV && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={this.toggleDetails}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-700">
                      Error Details (for developers)
                    </span>
                    {showDetails ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {showDetails && (
                    <div className="p-4 bg-gray-900 text-gray-100">
                      {/* Copy Button */}
                      <div className="flex justify-end mb-3">
                        <button
                          onClick={this.copyErrorToClipboard}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                            copied 
                              ? 'bg-green-600 text-white' 
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {copied ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span className="text-sm">Copy Error</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Error Details */}
                      <div className="space-y-4 font-mono text-xs overflow-x-auto">
                        {/* Error Message */}
                        <div>
                          <p className="text-gray-400 mb-1">Error Message:</p>
                          <p className="text-red-400">
                            {error?.message || 'No error message available'}
                          </p>
                        </div>

                        {/* Stack Trace */}
                        {error?.stack && (
                          <div>
                            <p className="text-gray-400 mb-1">Stack Trace:</p>
                            <pre className="text-gray-300 whitespace-pre-wrap break-all">
                              {error.stack}
                            </pre>
                          </div>
                        )}

                        {/* Component Stack */}
                        {errorInfo?.componentStack && (
                          <div>
                            <p className="text-gray-400 mb-1">Component Stack:</p>
                            <pre className="text-gray-300 whitespace-pre-wrap break-all">
                              {errorInfo.componentStack}
                            </pre>
                          </div>
                        )}

                        {/* Additional Info */}
                        <div>
                          <p className="text-gray-400 mb-1">Additional Information:</p>
                          <div className="text-gray-300 space-y-1">
                            <p>Time: {new Date().toISOString()}</p>
                            <p>URL: {window.location.href}</p>
                            <p>Browser: {navigator.userAgent}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Help Text */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Need help?</strong> If this error persists, please contact our support team.
                    {import.meta.env.DEV && (
                      <> You can copy the error information by expanding the details section and clicking the copy button.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}